/**
 * Tanvier : estimation de livraison datée.
 *
 * Affiche « Livraison estimée entre le mardi 14 et le vendredi 24 octobre » à partir des
 * réglages du thème (groupe « Tanvier — livraison et retours ») : jours de préparation et
 * jours de transport, comptés du lundi au vendredi, à partir de la date du jour à Paris.
 *
 * - Le calcul se fait dans le navigateur, car une date écrite en Liquid serait figée par le cache.
 * - Sans JavaScript (ou si une donnée manque), le texte de repli écrit par Liquid reste affiché.
 * - Le jour de la commande n'est pas compté : le premier jour compté est le jour ouvré suivant.
 * - Jours fériés : non exclus par défaut (ils comptent comme des jours du lundi au vendredi).
 *   Pour en exclure, renseigner data-excluded-dates (dates AAAA-MM-JJ séparées par des virgules),
 *   alimenté par le réglage « Jours à ne pas compter » du bloc Tanvier.
 * - Aucune dépendance, aucune erreur console si un élément manque.
 *
 * Balisage attendu (voir snippets/tanvier-delivery-estimate.liquid) :
 * <tanvier-delivery-estimate data-processing-min="1" data-processing-max="2"
 *   data-transit-min="7" data-transit-max="11" data-prefix="Livraison estimée">
 *   <span data-tv-estimate-text>Texte de repli</span>
 * </tanvier-delivery-estimate>
 *
 * Préparation 1 à 2 jours + transport 7 à 11 jours = 8 à 13 jours du lundi au vendredi : mêmes dates
 * que la page de paiement Shopify (préparation 1 jour + transit 7 à 12 jours).
 * Pas d'aria-live : la date est calculée une seule fois au chargement, sans action du visiteur.
 */

const TIME_ZONE = 'Europe/Paris';
const MAX_DAYS = 400;
const WEEKDAYS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MONTHS_FR = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/**
 * Lit un entier positif ou nul. Renvoie null si la valeur est absente ou invalide.
 * @param {string | undefined | null} value
 * @returns {number | null}
 */
function readCount(value) {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const number = Number(String(value).trim());
  if (!Number.isInteger(number) || number < 0 || number > 120) return null;
  return number;
}

/**
 * Date du jour (calendrier de Paris), à minuit UTC pour éviter les effets d'heure d'été.
 * @param {Date} [now]
 * @returns {Date}
 */
export function todayInParis(now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    /** @param {string} type */
    const get = (type) => Number(parts.find((part) => part.type === type)?.value);
    const year = get('year');
    const month = get('month');
    const day = get('day');
    if (year && month && day) return new Date(Date.UTC(year, month - 1, day));
  } catch (error) {
    // Fuseau non pris en charge par le navigateur : on garde la date locale.
  }
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/**
 * @param {Date} date
 * @returns {string} AAAA-MM-JJ
 */
function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * Lit la liste des jours à ne pas compter.
 * @param {string | undefined} value
 * @returns {Set<string>}
 */
export function parseExcludedDates(value) {
  const matches = String(value || '').match(/\d{4}-\d{2}-\d{2}/g);
  return new Set(matches || []);
}

/**
 * Ajoute un nombre de jours du lundi au vendredi (hors jours exclus) à une date.
 * Le jour de départ n'est pas compté.
 * @param {Date} start
 * @param {number} count
 * @param {Set<string>} excluded
 * @returns {Date}
 */
export function addWeekdays(start, count, excluded) {
  const date = new Date(start.getTime());
  let added = 0;
  let guard = 0;

  while (added < count && guard < MAX_DAYS) {
    date.setUTCDate(date.getUTCDate() + 1);
    guard += 1;

    const weekday = date.getUTCDay();
    if (weekday === 0 || weekday === 6) continue;
    if (excluded.has(toIsoDate(date))) continue;

    added += 1;
  }

  return date;
}

/**
 * Calcule les dates minimale et maximale de livraison.
 * @param {{ processingMin: number, processingMax: number, transitMin: number, transitMax: number, excluded?: Set<string>, today?: Date }} options
 * @returns {{ min: Date, max: Date }}
 */
export function computeEstimate({ processingMin, processingMax, transitMin, transitMax, excluded, today }) {
  const start = today || todayInParis();
  const skip = excluded || new Set();
  const minDays = Math.min(processingMin, processingMax) + Math.min(transitMin, transitMax);
  const maxDays = Math.max(processingMin, processingMax) + Math.max(transitMin, transitMax);

  return {
    min: addWeekdays(start, minDays, skip),
    max: addWeekdays(start, Math.max(minDays, maxDays), skip),
  };
}

/**
 * Découpe une date en jour de la semaine, quantième et mois, en français.
 * @param {Date} date
 * @returns {{ weekday: string, day: string, month: string, year: number }}
 */
export function frenchDateParts(date) {
  let weekday = WEEKDAYS_FR[date.getUTCDay()] || '';
  let month = MONTHS_FR[date.getUTCMonth()] || '';

  try {
    const parts = new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).formatToParts(date);
    weekday = parts.find((part) => part.type === 'weekday')?.value || weekday;
    month = parts.find((part) => part.type === 'month')?.value || month;
  } catch (error) {
    // Intl indisponible : les noms français ci-dessus servent de repli.
  }

  const dayNumber = date.getUTCDate();
  return {
    weekday,
    day: dayNumber === 1 ? '1er' : String(dayNumber),
    month,
    year: date.getUTCFullYear(),
  };
}

/**
 * Texte complet, utile pour les tests et les lecteurs d'écran.
 * @param {string} prefix
 * @param {Date} min
 * @param {Date} max
 * @returns {string}
 */
export function formatEstimate(prefix, min, max) {
  return buildSegments(prefix, min, max)
    .map((segment) => (typeof segment === 'string' ? segment : segment.label))
    .join('');
}

/**
 * @typedef {{ label: string, iso: string }} DateSegment
 */

/**
 * Prépare le texte en morceaux : chaînes simples et dates (affichées dans <time>).
 * @param {string} prefix
 * @param {Date} min
 * @param {Date} max
 * @returns {Array<string | DateSegment>}
 */
function buildSegments(prefix, min, max) {
  const first = frenchDateParts(min);
  const last = frenchDateParts(max);
  const lead = prefix ? `${prefix} ` : '';

  if (toIsoDate(min) === toIsoDate(max)) {
    return [`${lead}le `, { label: `${last.weekday} ${last.day} ${last.month}`, iso: toIsoDate(max) }];
  }

  const sameMonth = first.month === last.month && first.year === last.year;
  const firstLabel = sameMonth ? `${first.weekday} ${first.day}` : `${first.weekday} ${first.day} ${first.month}`;

  return [
    `${lead}entre le `,
    { label: firstLabel, iso: toIsoDate(min) },
    ' et le ',
    { label: `${last.weekday} ${last.day} ${last.month}`, iso: toIsoDate(max) },
  ];
}

/**
 * Remplit un élément d'estimation. Ne fait rien si une donnée manque.
 * @param {HTMLElement} element
 */
function renderEstimate(element) {
  const target = element.querySelector('[data-tv-estimate-text]');
  if (!(target instanceof HTMLElement)) return;

  const { dataset } = element;
  const processingMin = readCount(dataset.processingMin);
  const processingMax = readCount(dataset.processingMax);
  const transitMin = readCount(dataset.transitMin);
  const transitMax = readCount(dataset.transitMax);

  if (processingMin === null || processingMax === null || transitMin === null || transitMax === null) return;

  try {
    const { min, max } = computeEstimate({
      processingMin,
      processingMax,
      transitMin,
      transitMax,
      excluded: parseExcludedDates(dataset.excludedDates),
    });

    const fragment = document.createDocumentFragment();
    for (const segment of buildSegments(dataset.prefix || 'Livraison estimée', min, max)) {
      if (typeof segment === 'string') {
        fragment.append(segment);
      } else {
        const time = document.createElement('time');
        time.dateTime = segment.iso;
        const strong = document.createElement('strong');
        strong.textContent = segment.label;
        time.append(strong);
        fragment.append(time);
      }
    }

    target.replaceChildren(fragment);
    element.dataset.estimateReady = 'true';
  } catch (error) {
    // En cas de souci imprévu, le texte de repli reste affiché.
  }
}

if (typeof window !== 'undefined' && 'customElements' in window && typeof HTMLElement !== 'undefined') {
  class TanvierDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      renderEstimate(this);
    }
  }

  if (!customElements.get('tanvier-delivery-estimate')) {
    customElements.define('tanvier-delivery-estimate', TanvierDeliveryEstimate);

    // Page restaurée depuis le cache du navigateur (retour arrière) : on recalcule la date du jour.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      for (const element of document.querySelectorAll('tanvier-delivery-estimate')) {
        if (element instanceof HTMLElement) renderEstimate(element);
      }
    });
  }
}
